import { put, race, select, takeEvery, delay } from 'redux-saga/effects';
import { createMatchSelector } from 'connected-react-router';
import {
  append,
  assoc,
  path,
  pipe,
  prop,
  isEmpty,
  applySpec,
  filter,
  compose,
  not,
  pathEq,
  anyPass,
} from 'ramda';
import { startSubmit, stopSubmit, getFormValues } from 'redux-form';
import moment from 'moment';
import ReactGA from 'react-ga';

import { TIMING_CATEGORIES, TIMING_VARS } from 'services/googleAnalytics';
import {
  deviceStatusesSubmit,
  requestObservationUpdate,
} from 'store/actionCreators/deviceStatuses';
import { getReportItemData } from 'store/selectors/reports/singleItems';
import { getPractitionerListByPatient } from 'store/selectors/newReport/index';
import { getUserName } from 'store/selectors/user/index';

import { FORM_NAMES } from 'constants/forms';
import { PRELIMINARY } from 'constants/observationStatuses';

import { createSagaRequestRace } from 'utilities/request';
import { requestSendReviewReport } from 'store/actionCreators/notification';

function createCode(code, display) {
  return { coding: [{ code, display }] };
}

export function updateObservation(
  {
    devicestatus,
    patientInstructions,
    phone,
    physicianRecommendations,
    therapy,
    therapyText,
    comments,
    practitioners: performer,
    doctor,
  },
  data,
) {
  const component = prop('component', data);
  const updatedComponent = pipe(
    append({ code: createCode('device_status', 'Device status'), valueString: devicestatus }),
    append({
      code: createCode('device_status_notes', 'Device status notes'),
      valueString: patientInstructions,
    }),
    append({
      code: createCode('device_status_recommendations', 'Device status recommendations'),
      valueString: physicianRecommendations,
    }),
    arr =>
      !isEmpty(phone)
        ? append({
            code: createCode('device_status_phone_number', 'Phone number'),
            valueString: phone,
          })(arr)
        : arr,
    append({
      code: createCode('medical_therapy_status', 'Medical therapy status'),
      valueString: therapy,
    }),
    append({
      code: createCode('medical_therapy_status_text', 'Medical therapy status text'),
      valueString: therapyText,
    }),
    append({
      code: createCode('comments', 'Comments'),
      valueString: comments,
    }),
    append({
      code: createCode('reviewed_datetime', 'Reviewed date & time'),
      valueDateTime: new Date().toISOString(),
    }),
    append({
      code: createCode('reviewed_by', 'Reviewed by'),
      valueString: doctor,
    }),
  )(component);

  return pipe(
    assoc('component', updatedComponent),
    assoc('status', PRELIMINARY),
    assoc('performer', performer),
  )(data);
}

export function* onDeviceStatusesSubmit({ payload: { initTime } }) {
  yield put(startSubmit(FORM_NAMES.deviceStatusPicker));
  const formData = yield select(getFormValues(FORM_NAMES.deviceStatusPicker));

  const match = yield select(
    createMatchSelector('/patient/:id/transmission-report/:observationId'),
  );

  const { observationId, patientId } = applySpec({
    observationId: path(['params', 'observationId']),
    patientId: path(['params', 'id']),
  })(match);
  const data = yield select(getReportItemData(observationId));
  data.component = filter(
    compose(
      not,
      anyPass([
        pathEq(['code', 'coding', '0', 'display'], 'device_status_recommendations'),
        pathEq(['code', 'coding', '0', 'display'], 'device_status_notes'),
        pathEq(['code', 'coding', '0', 'code'], 'comments'),
      ]),
    ),
    data.component,
  );
  const practitioners = yield select(getPractitionerListByPatient);
  const doctor = yield select(getUserName);
  const updatedObservation = updateObservation({ ...formData, practitioners, doctor }, data);

  yield put(requestObservationUpdate({ data: updatedObservation, id: observationId }));
  yield race(createSagaRequestRace(requestObservationUpdate));

  ReactGA.timing({
    category: TIMING_CATEGORIES.forms,
    variable: TIMING_VARS.verifyReport,
    value: moment().diff(initTime),
  });

  yield put(stopSubmit(FORM_NAMES.deviceStatusPicker));
  yield delay(3000);
  yield put(requestSendReviewReport({ observationId, patientId }));
}

export default function*() {
  yield takeEvery(deviceStatusesSubmit, onDeviceStatusesSubmit);
}
